import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { Search } from '@/icons/Search';
import { ChevronRight } from '@/icons/ChevronRight';
import { Button } from './ui/button';
import { City, District } from '@/models/City';
import { Spinner } from './ui/spinner';
import { SearchX, X } from 'lucide-react';
import { ScreenSize, useScreenSize } from '@/lib/hook/useScreenSize';
import { Drawer, DrawerContent, DrawerTrigger } from './ui/drawer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from './ui/dropdown-menu';
import { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { Location } from '@/icons/Location';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

export default function CityChooserPopover({
  isOpen,
  onOpenChange,
  triggerText,
  searchDistrict,
  chooseDistrictVisible,
  cities,
  visibleDistricts,
  selectDistrict,
  enableDisctrictChooser,
  closeDistrictChooser,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  triggerText: string;
  searchDistrict: (value: string) => void;
  chooseDistrictVisible: boolean;
  cities: City[];
  visibleDistricts: District[];
  selectDistrict: (district: District) => void;
  enableDisctrictChooser: (id: number) => void;
  closeDistrictChooser: () => void;
}) {
  const screenSize = useScreenSize();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number>(0);

  // 🔧 IMPROVED: More robust width calculation with useLayoutEffect
  useLayoutEffect(() => {
    if (!triggerRef.current) return;

    const updateWidth = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const newWidth = Math.max(rect.width, 400); // Ensure minimum 400px
        setTriggerWidth(newWidth);
      }
    };

    // Immediate width calculation
    updateWidth();

    const resizeObserver = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        updateWidth();
      });
    });

    resizeObserver.observe(triggerRef.current);
    window.addEventListener('resize', updateWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // Memoized search handler
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      searchDistrict(e.target.value);
    },
    [searchDistrict],
  );

  const ChooserTrigger = () => {
    return (
      <Button
        onClick={() => onOpenChange(!isOpen)}
        className="flex flex-row bg-white hover:bg-white border-[5px] border-[#ECEAE1] w-full rounded-[16px] px-4 py-8 justify-between items-center gap-4 text-start"
        aria-label="Teslimat adresi seç"
      >
        <div className="flex flex-row gap-2 items-center">
          <Location className="text-[#2B3280] size-[24px]" />
          <span className="text-[#212121] text-[16px] font-medium whitespace-normal">
            {triggerText}
          </span>
        </div>
        <ChevronRight
          className={`text-base text-[12px] text-[#212121] transition-transform duration-200 ${
            isOpen ? '-rotate-90' : 'rotate-90'
          }`}
        />
      </Button>
    );
  };

  if (screenSize === ScreenSize.SM) {
    return (
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetTrigger asChild>
          <div className="w-full" ref={triggerRef}>
            <ChooserTrigger />
          </div>
        </SheetTrigger>
        <SheetContent className="bg-[#2B3280] border-none p-0" side="bottom" closeEnabled={false}>
          <div className="flex flex-col p-2">
            <div className="flex flex-row items-center justify-center border-b-2 border-[#4D5394] pb-2 relative h-12">
              <span className="text-[16px] w-fit text-white font-medium">
                Gönderim Bölgesi Seçin
              </span>
              <Button
                className="text-white hover:bg-transparent absolute right-0 bg-transparent"
                onClick={() => onOpenChange(false)}
              >
                <X className="text-white size-6" />
              </Button>
            </div>
            <div className="relative w-full mb-4 px-2 mt-4">
              <Input
                type="text"
                className="w-full border-none bg-[#F5F5F0] placeholder:text-[#5C5C57] rounded-full h-[32px] text-sm placeholder:font-normal placeholder:text-sm pl-4"
                placeholder="Gönderim bölgesi ara..."
                disabled={cities.length === 0}
                onChange={handleSearchChange}
              />
            </div>
            {chooseDistrictVisible ? (
              <div
                className="flex flex-row gap-4 hover:cursor-pointer group items-center"
                onClick={closeDistrictChooser}
              >
                <ChevronRight className="text-[#B2B4CE] text-[11px] group-hover:translate-x-2 transition-transform duration-200 rotate-180" />
                <span className="text-[#B2B4CE] text-[11px] font-medium underline">Geri Dön</span>
              </div>
            ) : (
              <span className="text-hzc_purple_300 text-sm sm:text-base md:text-lg">
                Şehir Seçin
              </span>
            )}
            <ScrollArea className="h-72 pt-2">
              {chooseDistrictVisible ? (
                <div className="flex flex-col gap-2">
                  <span className="text-[#B2B4CE] text-[16px] font-medium mt-2 px-2">
                    Mahalle seçin
                  </span>
                  {cities.length === 0 ? (
                    <DistrictLoading />
                  ) : (
                    <>
                      {visibleDistricts.length === 0 ? (
                        <EmptyDistrictLayout />
                      ) : (
                        <>
                          {visibleDistricts.map((district) => (
                            <div
                              key={district.id}
                              onClick={() => selectDistrict(district)}
                              className="flex flex-row items-center gap-2 py-3 text-[14px] font-normal text-white w-full border-b border-[#4D5394] cursor-pointer"
                            >
                              {district.title}
                              <ChevronRight className="size-4 absolute right-4" />
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <>
                  {cities.length === 0 ? (
                    <DistrictLoading />
                  ) : (
                    <>
                      {cities.map((city) => (
                        <div
                          key={city.id}
                          onClick={() => enableDisctrictChooser(city.id)}
                          className="flex flex-row items-center gap-2 py-3 text-[14px] font-normal text-white w-full border-b border-[#4D5394] cursor-pointer"
                        >
                          {city.title}
                          <ChevronRight className="size-4 absolute right-4" />
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className="relative w-full">
      <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          <div className="w-full" ref={triggerRef}>
            <ChooserTrigger />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={2}
          className="rounded-lg border-2 border-[#ECEAE1] p-0 !w-full" // 🔧 ADDED: !w-full to force full width
          style={{
            width: triggerWidth > 0 ? `${triggerWidth}px` : '100%', // 🔧 IMPROVED: auto → 100%
            minWidth: triggerWidth > 0 ? `${triggerWidth}px` : '400px', // 🔧 INCREASED: Use trigger width as minimum
            maxWidth: 'none', // 🔧 REMOVED: max width constraint to allow full expansion
          }}
          // 🔧 ADDED: Force full width using data attributes
          data-width={triggerWidth}
        >
          <div className="flex flex-col p-4">
            {' '}
            {/* 🔧 INCREASED: p-2 → p-4 for better spacing */}
            <div className="relative w-full mb-4">
              <Input
                type="text"
                className="w-full border-none bg-[#F5F5F0] placeholder:text-[#5C5C57] rounded-full h-[32px] text-sm placeholder:font-normal placeholder:text-sm pl-12"
                placeholder="Gönderim bölgesi ara..."
                disabled={cities.length === 0}
                onChange={handleSearchChange}
              />
              <div className="absolute top-0 left-4 flex items-center h-full">
                <Search className="text-hzc_beige_700 text-xl" />
              </div>
            </div>
            {chooseDistrictVisible ? (
              <div
                className="flex flex-row gap-4 hover:cursor-pointer group items-center mb-2" // 🔧 ADDED: mb-2 for spacing
                onClick={closeDistrictChooser}
              >
                <ChevronRight className="text-[#5C5C57] text-[11px] group-hover:translate-x-2 transition-transform duration-200 rotate-180" />
                <span className="text-[#212121] text-[11px] font-medium underline">Geri Dön</span>
              </div>
            ) : (
              <span className="text-[#5C5C57] text-[16px] font-medium mb-2">Şehir Seçin</span> // 🔧 ADDED: mb-2 for spacing
            )}
            <ScrollArea className="h-72">
              {chooseDistrictVisible ? (
                <div className="flex flex-col gap-1">
                  {' '}
                  {/* 🔧 REDUCED: gap-2 → gap-1 for tighter layout */}
                  <span className="text-[#5C5C57] text-[16px] font-medium mt-2 mb-2">
                    Mahalle seçin
                  </span>
                  {cities.length === 0 ? (
                    <DistrictLoading />
                  ) : (
                    <>
                      {visibleDistricts.length === 0 ? (
                        <EmptyDistrictLayout />
                      ) : (
                        <>
                          {visibleDistricts.map((district) => (
                            <div
                              key={district.id}
                              className="flex flex-row items-center justify-between group py-3 px-3 hover:cursor-pointer hover:bg-hzc_beige_100 rounded-lg transition-colors duration-150" // 🔧 INCREASED: py-2 → py-3, px-2 → px-3
                              onClick={() => selectDistrict(district)}
                            >
                              <span className="text-[#212121] text-[12px] font-medium">
                                {' '}
                                {/* 🔧 INCREASED: text-[11px] → text-[12px] */}
                                {district.title}
                              </span>
                              <ChevronRight className="text-[#212121] text-[11px] group-hover:-translate-x-2 transition-transform duration-200" />
                            </div>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  {' '}
                  {/* 🔧 REDUCED: gap-2 → gap-1 */}
                  {cities.length === 0 ? (
                    <DistrictLoading />
                  ) : (
                    <>
                      {cities.map((city) => (
                        <div
                          key={city.id}
                          className="flex flex-row items-center justify-between group py-3 px-3 hover:cursor-pointer hover:bg-[#F5F5F0] rounded-[8px] transition-colors duration-150" // 🔧 INCREASED: py-2 → py-3, px-2 → px-3
                          onClick={() => enableDisctrictChooser(city.id)}
                        >
                          <span className="text-[#212121] text-[13px] font-medium">
                            {' '}
                            {/* 🔧 INCREASED: text-[12px] → text-[13px] */}
                            {city.title}
                          </span>
                          <ChevronRight className="text-[#212121] text-[11px] group-hover:-translate-x-2 transition-transform duration-200" />
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function EmptyDistrictLayout() {
  return (
    <div className="flex flex-col gap-4 justify-center w-full py-8">
      {' '}
      {/* 🔧 ADDED: py-8 for better spacing */}
      <SearchX className="mx-auto size-8 text-hzc_purple_500" />
      <span className="text-sm font-medium text-hzc_beige_900 text-center px-4">
        {' '}
        {/* 🔧 ADDED: px-4 for padding */}
        Aradığınız isime uygun bir gönderim bölgesi bulunamadı
      </span>
    </div>
  );
}

export function DistrictLoading() {
  return (
    <div className="flex flex-col gap-2 justify-center w-full py-8">
      {' '}
      {/* 🔧 ADDED: py-8 for better spacing */}
      <Spinner className="mx-auto size-8 text-hzc_purple_500" />
      <span className="text-sm font-medium text-white text-center sm:text-hzc_beige_900">
        Yükleniyor...
      </span>
    </div>
  );
}
